// Barre latérale (état : hooks/useSidebar). Données : ReactShell::Sidebar.
//
// Sélecteurs lus ailleurs, à garder tels quels : [data-onboarding=…] (tour
// guidé), .sidebar-menu-link[href] .sidebar-icon.undiscovered-glow (tours de
// fonctionnalité), .sidebar-menu-item.has-submenu.active, body.has-sidebar.
import type { MouseEvent } from 'react'
import { usePage } from '@inertiajs/react'
import AppLink from '@/components/ui/AppLink'
import type { SidebarData } from '@/components/shell/sidebar/types'
import { useShared } from '@/hooks/useShared'
import { useSidebar } from '@/hooks/useSidebar'
import { t } from '@/lib/i18n'

const SUBMENU_LABELS: Record<string, [string, string]> = {
  account: ['sidebar.account', 'Account'],
  portfolio: ['sidebar.portfolio', 'Portfolio'],
  investor_profile: ['sidebar.investor_profile', 'Investor Profile'],
  opportunities: ['sidebar.opportunities', 'Opportunities'],
  api: ['account.api.tab', 'API'],
}

const pathOf = (href: string) => href.split(/[?#]/, 1)[0]

export default function Sidebar({ data }: { data: Record<string, any> }) {
  const d = data as SidebarData
  const { csrfToken } = useShared()
  const currentPath = pathOf(usePage().url)
  const defaultOpen = d.preferences.active ? 'preferences' : d.company?.active ? 'company' : null
  const sidebar = useSidebar(defaultOpen)

  // Lien actif : chemin exact de la page, calculé côté client.
  const linkClass = (base: string, href: string) => (pathOf(href) === currentPath ? `${base} active` : base)
  const glow = (key: string) => (d.undiscovered.includes(key) ? 'undiscovered-glow' : '')
  const submenuItemClass = (key: string, active: boolean) =>
    ['sidebar-menu-item has-submenu', active && 'active', sidebar.openKey === key && 'open'].filter(Boolean).join(' ')
  const onArrow = (key: string) => (event: MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    sidebar.toggleSubmenu(key)
  }

  return (
    <>
      <aside className={sidebar.collapsed ? 'app-sidebar collapsed' : 'app-sidebar'}>
        {/* Sidebar Header */}
        <div className="sidebar-header">
          <AppLink href={d.homePath} className="sidebar-brand">
            <img src="/images/Logo_hor_transp.png" alt="Northvane" className="sidebar-logo" />
          </AppLink>
        </div>

        {/* Sidebar Navigation */}
        <nav className="sidebar-nav">
          {/* CATEGORY 1: PORTFOLIO & OPPORTUNITIES */}
          <div className="sidebar-category">
            <div className="sidebar-category-header">
              <i className="fas fa-chart-line sidebar-category-icon"></i>{' '}
              <span className="sidebar-category-title">{t('sidebar.category_stocks', { default: 'Stock Market' })}</span>
            </div>
            <ul className="sidebar-menu">
              {/* Overview */}
              <li className="sidebar-menu-item">
                <AppLink href={d.dashboardPath} className={linkClass('sidebar-menu-link', d.dashboardPath)}>
                  <i className="fas fa-chart-pie sidebar-icon"></i>{' '}
                  <span className="sidebar-text">{t('sidebar.overview', { default: 'Overview' })}</span>
                  {d.portfolioRefreshUnseen && (
                    <>
                      {' '}
                      <span className="sidebar-badge">1</span>
                    </>
                  )}
                </AppLink>
              </li>
              {/* Opportunities, then Stock Analysis: the two key actions (find
                  an idea, check a stock), set apart. Both glow until opened,
                  and again after the weekend updates (User::DISCOVERABLE_PAGES). */}
              <li className="sidebar-menu-item">
                <AppLink href={d.opportunitiesPath} className={linkClass('sidebar-menu-link', d.opportunitiesPath)} data-onboarding="watch-tab">
                  <i className={`fas fa-lightbulb sidebar-icon ${glow('opportunities')}`}></i>{' '}
                  <span className="sidebar-text">{t('sidebar.opportunities', { default: 'Opportunities' })}</span>
                </AppLink>
              </li>

              <li className="sidebar-menu-divider" aria-hidden="true"></li>

              {/* Stock Analysis */}
              <li className="sidebar-menu-item">
                <AppLink href={d.stockAnalysisPath} className={linkClass('sidebar-menu-link', d.stockAnalysisPath)}>
                  <i className={`fas fa-microscope sidebar-icon ${glow('stock_analysis')}`}></i>{' '}
                  <span className="sidebar-text">{t('sidebar.stock_analysis', { default: 'Stock Analysis' })}</span>
                </AppLink>
              </li>

              {d.graphAnalyses && (
                // Graph Analyzer
                <li className="sidebar-menu-item">
                  <AppLink href={d.graphAnalysesPath} className={linkClass('sidebar-menu-link', d.graphAnalysesPath)}>
                    <i className={`fas fa-chart-area sidebar-icon ${glow('graph_analyses')}`}></i>{' '}
                    <span className="sidebar-text">{t('sidebar.graph_analyzer', { default: 'Chart Analyzer' })}</span>
                    {d.graphAnalyses.unseenCount > 0 && (
                      <>
                        {' '}
                        <span className="sidebar-badge">{d.graphAnalyses.unseenCount}</span>
                      </>
                    )}
                  </AppLink>
                </li>
              )}

              {d.simulations && (
                // Simulator
                <li className="sidebar-menu-item">
                  <AppLink href={d.simulationsPath} className={linkClass('sidebar-menu-link', d.simulationsPath)}>
                    <i className={`fas fa-flask sidebar-icon ${glow('simulations')}`}></i>{' '}
                    <span className="sidebar-text">{t('sidebar.simulator', { default: 'Simulator' })}</span>
                    {d.simulations.unseenCount > 0 && (
                      <>
                        {' '}
                        <span className="sidebar-badge">{d.simulations.unseenCount}</span>
                      </>
                    )}
                  </AppLink>
                </li>
              )}
            </ul>
          </div>

          {/* CATEGORY 2: MARKETS & POLITICS */}
          <div className="sidebar-category">
            <div className="sidebar-category-header">
              <i className="fas fa-globe sidebar-category-icon"></i>{' '}
              <span className="sidebar-category-title">{t('sidebar.category_geosocial', { default: 'Markets & Politics' })}</span>
            </div>
            <ul className="sidebar-menu">
              {/* Market Radar (Pro): sentiment + geopolitics on one page. The
                  tour's two steps (social-sentiment-mini, geopolitical-mini)
                  both lead there: the item carries one, its link the other. */}
              <li className="sidebar-menu-item" data-onboarding="geopolitical-mini">
                <AppLink href={d.marketRadarPath} className={linkClass('sidebar-menu-link', d.marketRadarPath)} data-onboarding="social-sentiment-mini">
                  <i className="fas fa-satellite-dish sidebar-icon"></i>{' '}
                  <span className="sidebar-text">{t('market_radar.title', { default: 'Market Radar' })}</span>
                </AppLink>
              </li>
              {/* US Congress (open to every account) */}
              <li className="sidebar-menu-item">
                <AppLink href={d.congressPath} className={linkClass('sidebar-menu-link', d.congressPath)} data-onboarding="congress-link">
                  <i className="fas fa-landmark sidebar-icon"></i>{' '}
                  <span className="sidebar-text">{t('congress.title', { default: 'US Congress' })}</span>
                </AppLink>
              </li>
            </ul>
          </div>

          {/* CATEGORY 3: PREDICTION MARKET */}
          <div className="sidebar-category">
            <div className="sidebar-category-header">
              <i className="fas fa-percent sidebar-category-icon"></i>{' '}
              <span className="sidebar-category-title">{t('sidebar.category_predictions', { default: 'Prediction Market' })}</span>
            </div>
            <ul className="sidebar-menu">
              {/* Polymarket & Kalshi */}
              <li className="sidebar-menu-item">
                <AppLink href={d.predictionMarketsPath} className={linkClass('sidebar-menu-link', d.predictionMarketsPath)}>
                  <i className={`fas fa-balance-scale sidebar-icon ${glow('prediction_markets')}`}></i>{' '}
                  <span className="sidebar-text">{t('sidebar.polymarket', { default: 'Polymarket & Kalshi' })}</span>{' '}
                  <span className="sidebar-badge-beta">Beta</span>
                </AppLink>
              </li>
            </ul>
          </div>

          {/* BLOG (dernier echelon) */}
          <div className="sidebar-category sidebar-category-blog">
            <ul className="sidebar-menu">
              <li className="sidebar-menu-item">
                <AppLink href={d.blogPath} className={linkClass('sidebar-menu-link', d.blogPath)}>
                  <i className="fas fa-newspaper sidebar-icon"></i>{' '}
                  <span className="sidebar-text">{t('sidebar.blog', { default: 'Blog' })}</span>
                </AppLink>
              </li>
            </ul>
          </div>
        </nav>

        {/* Sidebar Footer */}
        <div className="sidebar-footer">
          {/* User block */}
          <div className="sidebar-user">
            <div className="sidebar-user-main">
              <div className="sidebar-user-avatar">
                <i className="fas fa-user"></i>
              </div>{' '}
              <div className="sidebar-user-info">
                <span className="sidebar-user-name">{d.userName}</span>{' '}
                <span className="sidebar-user-plan">{d.userPlan}</span>
              </div>
            </div>
          </div>

          {/* Settings menus (dropdown style) */}
          <ul className="sidebar-menu sidebar-footer-menu">
            <li className={submenuItemClass('preferences', d.preferences.active)}>
              <div className="sidebar-menu-link-wrapper">
                <AppLink
                  href={d.preferences.accountPath}
                  className={linkClass('sidebar-menu-link', d.preferences.accountPath)}
                  onClick={() => sidebar.openWithNavigation('preferences')}
                >
                  <i className="fas fa-user-cog sidebar-icon"></i>{' '}
                  <span className="sidebar-text">{t('nav.preferences')}</span>
                </AppLink>{' '}
                <button type="button" className="sidebar-arrow-btn" onClick={onArrow('preferences')}>
                  <i className="fas fa-chevron-down sidebar-arrow"></i>
                </button>
              </div>
              <ul className="sidebar-submenu">
                {d.preferences.links.map((link) => (
                  <li key={link.key}>
                    <AppLink href={link.href} className={linkClass('sidebar-submenu-link', link.href)}>
                      <i className={link.icon}></i>{' '}
                      <span>{t(SUBMENU_LABELS[link.key][0], { default: SUBMENU_LABELS[link.key][1] })}</span>
                    </AppLink>
                  </li>
                ))}
              </ul>
            </li>

            {d.company && (
              <li className={submenuItemClass('company', d.company.active)}>
                <div className="sidebar-menu-link-wrapper">
                  <AppLink
                    href={d.company.settingsPath}
                    className={linkClass('sidebar-menu-link', d.company.settingsPath)}
                    onClick={() => sidebar.openWithNavigation('company')}
                  >
                    <i className="fas fa-building sidebar-icon"></i>{' '}
                    <span className="sidebar-text">{t('sidebar.company', { default: 'Company' })}</span>
                  </AppLink>{' '}
                  <button type="button" className="sidebar-arrow-btn" onClick={onArrow('company')}>
                    <i className="fas fa-chevron-down sidebar-arrow"></i>
                  </button>
                </div>
                <ul className="sidebar-submenu">
                  <li>
                    <AppLink href={d.company.settingsPath} className={linkClass('sidebar-submenu-link', d.company.settingsPath)}>
                      <i className="fas fa-cog"></i>{' '}
                      <span>{t('sidebar.settings', { default: 'Settings' })}</span>
                    </AppLink>
                  </li>
                  <li>
                    {/* Nouvel onglet : lien simple. */}
                    <a href={d.company.apiDocsPath} className={linkClass('sidebar-submenu-link', d.company.apiDocsPath)} target="_blank">
                      <i className="fas fa-book"></i>{' '}
                      <span>{t('sidebar.api_docs', { default: 'API Documentation' })}</span>
                    </a>
                  </li>
                </ul>
              </li>
            )}
          </ul>

          {/* Déconnexion : formulaire natif, la session et le jeton CSRF
              changent, la page suivante se charge en entier. */}
          <form className="sidebar-logout-form" method="post" action={d.logoutPath}>
            <input type="hidden" name="_method" value="delete" autoComplete="off" />
            <button className="sidebar-logout" type="submit">
              <i className="fas fa-sign-out-alt"></i>{' '}
              <span className="sidebar-text">{t('nav.logout', { default: 'Logout' })}</span>
            </button>
            <input type="hidden" name="authenticity_token" value={csrfToken} autoComplete="off" />
          </form>
        </div>
      </aside>

      {/* Mobile sidebar overlay */}
      <div className="sidebar-overlay" onClick={sidebar.closeMobile}></div>
    </>
  )
}
